<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';

import PageLayout from 'dashboard/components-next/aiAgents/PageLayout.vue';
import AgentCard from 'dashboard/components-next/aiAgents/roster/AgentCard.vue';
import HireAgentForm from 'dashboard/components-next/aiAgents/roster/HireAgentForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { isAdmin } = useAdmin();

const hireDialogRef = ref(null);

const brains = useMapGetter('aiAgentBrains/getRecords');
const brainUiFlags = useMapGetter('aiAgentBrains/getUIFlags');
const integrationUiFlags = useMapGetter('integrations/getUIFlags');
const getIntegration = useMapGetter('integrations/getIntegration');

// D35: the module is double-gated — feature flag (route meta) AND the
// ai_agents integration hook. Without the hook we show an
// "enable the integration first" empty state instead of a dead roster.
const aiAgentsIntegration = computed(() => getIntegration.value('ai_agents'));
const isHookPresent = computed(
  () => (aiAgentsIntegration.value?.hooks || []).length > 0
);

const isFetching = computed(
  () => brainUiFlags.value.fetchingList || integrationUiFlags.value.isFetching
);

const sortedBrains = computed(() =>
  brains.value.slice().sort((a, b) => a.id - b.id)
);

onMounted(() => {
  store.dispatch('integrations/get');
  store.dispatch('aiAgentBrains/get');
});

const goToAgent = brain => {
  router.push({
    name: 'ai_agents_agent_show',
    params: { accountId: route.params.accountId, brainId: brain.id },
  });
};

const onHired = brain => {
  hireDialogRef.value?.close();
  if (brain?.id) goToAgent(brain);
};
</script>

<template>
  <PageLayout
    :header-title="t('AI_AGENTS.ROSTER.HEADER')"
    :is-fetching="isFetching"
    :is-empty="!isHookPresent || !sortedBrains.length"
  >
    <template #action>
      <Button
        v-if="isAdmin && isHookPresent && sortedBrains.length"
        :label="t('AI_AGENTS.ROSTER.HIRE_BUTTON')"
        icon="i-lucide-plus"
        size="sm"
        @click="hireDialogRef?.open()"
      />
    </template>

    <template #emptyState>
      <div
        v-if="!isHookPresent"
        class="flex flex-col items-center gap-3 py-16 text-center"
      >
        <h2 class="text-xl font-medium text-n-slate-12">
          {{ t('AI_AGENTS.ROSTER.NO_HOOK.TITLE') }}
        </h2>
        <p class="max-w-md text-sm text-n-slate-11">
          {{ t('AI_AGENTS.ROSTER.NO_HOOK.DESCRIPTION') }}
        </p>
        <RouterLink
          :to="{
            name: 'settings_applications',
            params: { accountId: route.params.accountId },
          }"
          class="text-sm text-n-blue-11 hover:underline"
        >
          {{ t('AI_AGENTS.ROSTER.NO_HOOK.CTA') }}
        </RouterLink>
      </div>
      <!-- M04: empty roster = the hire-first-agent screen, not a dead page. -->
      <div v-else class="py-12">
        <HireAgentForm @hired="onHired" />
      </div>
    </template>

    <template #body>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <AgentCard
          v-for="brain in sortedBrains"
          :key="brain.id"
          :brain="brain"
          @click="goToAgent(brain)"
        />
      </div>
    </template>

    <Dialog
      ref="hireDialogRef"
      :title="t('AI_AGENTS.HIRE.TITLE')"
      :show-confirm-button="false"
      :show-cancel-button="false"
    >
      <HireAgentForm @hired="onHired" />
    </Dialog>
  </PageLayout>
</template>
